import { Product, KeywordWithIntent, ViewType, DeduplicationStrategy, DeduplicationScope, DeduplicatedKeywordInstance, DeduplicationState } from '../types';

/**
 * Gets the display name for a group based on view type
 */
function getGroupName(product: Product, viewType: ViewType): string {
  switch (viewType) {
    case 'brand':
      return product.brand || 'Unknown Brand';
    case 'sub-brand':
      return product.subBrand || 'Unknown Sub-Brand';
    case 'product':
    default:
      // Use product ID to ensure each product is its own unique group
      // (multiple products can have the same name but different sub-brands)
      return product.id;
  }
}

/**
 * Analyzes keywords and identifies duplicates across products
 */
export function analyzeKeywordDuplicates(
  products: Product[],
  viewType: ViewType,
  scope: DeduplicationScope,
  strategy: DeduplicationStrategy
): DeduplicatedKeywordInstance[] {
  const instances: DeduplicatedKeywordInstance[] = [];

  // Collect all keyword instances with their context
  products.forEach(product => {
    const groupName = getGroupName(product, viewType);

    product.keywords.forEach(keyword => {
      instances.push({
        keyword: keyword.text,
        productId: product.id,
        productName: product.name,
        groupName: groupName,
        volume: keyword.volume || 0,
        intentScore: keyword.intent_score,
        intent: keyword.intent,
        isKept: false // Will be determined below
      });
    });
  });

  // Group instances by keyword text and scope
  const duplicateGroups = new Map<string, DeduplicatedKeywordInstance[]>();

  instances.forEach(instance => {
    // Create grouping key based on scope
    let key: string;
    if (scope === 'within-groups') {
      // Group by keyword + group (brand/sub-brand/product)
      key = `${instance.keyword}|||${instance.groupName}`;
    } else {
      // Group by keyword only (across all groups)
      key = instance.keyword;
    }

    if (!duplicateGroups.has(key)) {
      duplicateGroups.set(key, []);
    }
    duplicateGroups.get(key)!.push(instance);
  });

  // For each group of duplicates, determine which to keep
  duplicateGroups.forEach(groupInstances => {
    if (groupInstances.length <= 1) {
      // No duplicates, keep it
      if (groupInstances.length === 1) {
        groupInstances[0].isKept = true;
      }
      return;
    }

    // Sort to determine which instance to keep based on strategy
    let sortedInstances = [...groupInstances];
    
    switch (strategy) {
      case 'volume':
        sortedInstances.sort((a, b) => b.volume - a.volume);
        break;
      case 'intent-score':
        sortedInstances.sort((a, b) => {
          const scoreA = a.intentScore || 0;
          const scoreB = b.intentScore || 0;
          return scoreB - scoreA;
        });
        break;
      case 'first':
        // Keep original order (first product in the list)
        // Already in order from how we collected them
        break;
    }

    // Mark the first one as kept, rest as removed
    sortedInstances[0].isKept = true;
    for (let i = 1; i < sortedInstances.length; i++) {
      sortedInstances[i].isKept = false;
    }
    
    // Log a sample to verify strategy is working
    if (sortedInstances.length > 1 && Math.random() < 0.01) { // Log 1% of duplicates
      console.log(`[Strategy Test] Keyword: "${sortedInstances[0].keyword}"`);
      console.log(`  Strategy: ${strategy}`);
      console.log(`  Kept:`, { 
        product: sortedInstances[0].productName, 
        volume: sortedInstances[0].volume, 
        intent: sortedInstances[0].intentScore 
      });
      console.log(`  Removed:`, sortedInstances.slice(1).map(i => ({ 
        product: i.productName, 
        volume: i.volume, 
        intent: i.intentScore 
      })));
    }
  });

  return instances;
}

/**
 * Applies deduplication to products and returns modified products + backup
 */
export function applyDeduplication(
  products: Product[],
  viewType: ViewType,
  scope: DeduplicationScope,
  strategy: DeduplicationStrategy
): {
  modifiedProducts: Product[];
  deduplicationState: DeduplicationState;
} {
  // Analyze duplicates
  const duplicateInstances = analyzeKeywordDuplicates(products, viewType, scope, strategy);

  // Create backup of original keywords
  const originalProductKeywords: { [productId: string]: KeywordWithIntent[] } = {};
  products.forEach(product => {
    originalProductKeywords[product.id] = [...product.keywords];
  });

  // Build a set of keywords to keep per product
  const keywordsToKeep = new Map<string, Set<string>>(); // productId -> Set of keyword texts to keep
  
  duplicateInstances.forEach(instance => {
    if (instance.isKept) {
      if (!keywordsToKeep.has(instance.productId)) {
        keywordsToKeep.set(instance.productId, new Set());
      }
      keywordsToKeep.get(instance.productId)!.add(instance.keyword);
    }
  });

  // Create modified products with filtered keywords
  const modifiedProducts = products.map(product => {
    const groupName = getGroupName(product, viewType);
    
    // Get all instances for this product from the analysis
    const productInstances = duplicateInstances.filter(inst => inst.productId === product.id);
    
    // For within-groups scope, we also need to check if keywords were kept by other products in the same group
    const groupInstances = duplicateInstances.filter(inst => inst.groupName === groupName);
    
    // Build a map of keyword text -> which product kept it
    const keywordKeptBy = new Map<string, string>(); // keyword -> productId that kept it
    groupInstances.forEach(inst => {
      if (inst.isKept) {
        keywordKeptBy.set(inst.keyword, inst.productId);
      }
    });
    
    // Track which keyword texts we've already processed (to avoid keeping multiple instances)
    const processedKeywords = new Set<string>();
    const newKeywords: KeywordWithIntent[] = [];
    
    // Go through each keyword in the product
    product.keywords.forEach(kw => {
      const keywordText = kw.text;
      
      // Skip if we've already processed this keyword text
      if (processedKeywords.has(keywordText)) {
        return;
      }
      
      // Check who should keep this keyword
      const keptByProductId = keywordKeptBy.get(keywordText);
      
      if (!keptByProductId) {
        // No one keeps this keyword - it was removed entirely
        return;
      }
      
      if (keptByProductId !== product.id) {
        // Another product in the group keeps this keyword, remove from this product
        return;
      }
      
      // This product should keep this keyword
      newKeywords.push(kw);
      processedKeywords.add(keywordText);
    });
    
    return {
      ...product,
      keywords: newKeywords
    };
  });

  // Count removed keywords
  const removedCount = duplicateInstances.filter(inst => !inst.isKept).length;

  const deduplicationState: DeduplicationState = {
    isActive: true,
    strategy,
    scope,
    viewType,
    timestamp: new Date(),
    removedCount,
    originalProductKeywords,
    duplicateInstances
  };

  return {
    modifiedProducts,
    deduplicationState
  };
}

/**
 * Restores original keywords from backup
 */
export function restoreFromDeduplication(
  products: Product[],
  deduplicationState: DeduplicationState
): Product[] {
  return products.map(product => ({
    ...product,
    keywords: deduplicationState.originalProductKeywords[product.id] || product.keywords
  }));
}

/**
 * Gets count of removed keywords for a specific product
 */
export function getRemovedKeywordCount(
  productId: string,
  deduplicationState: DeduplicationState | null
): number {
  if (!deduplicationState) return 0;
  
  return deduplicationState.duplicateInstances.filter(
    inst => inst.productId === productId && !inst.isKept
  ).length;
}
